import { useMemo, useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { LayoutGrid, List, Plus, Upload } from 'lucide-react';
import { Search, Tools as ToolsIcon } from 'iconoir-react';

import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from 'components/ui/alert-dialog';
import {
  IGetToolsResponse,
  ITool,
  ToolStatus,
  ToolType,
} from 'features/chat/agent-chat-module/services/gpt-chat.service';
import { useDeleteTool, useGetOpenTools, useGetTools } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import { useToast } from 'hooks/use-toast';
import { useTranslation } from 'react-i18next';
import { useConfigStore } from 'state/store/config/useConfigStore';
import { cn } from 'lib/utils';
import { AddMcpServerModal } from './components/add-mcp-server-modal';
import { ConnectorDetailsModal } from './components/connector-details-modal';
import { OpenConnectorDetailsModal } from './components/open-connector-details-modal';
import { EditComposioToolModal } from './components/edit-composio-tool-modal';
import { EditOpenConnectorToolModal } from './components/edit-open-connector-tool-modal';
import { EditToolModal } from './components/edit-tool-modal';
import { ImportMcpServersModal } from './components/import-mcp-servers-modal';
import { BlocksToolsList } from './blocks-tools-list';
import { ToolsList } from './tools-list';
import type { ViewMode } from './tools-shared';
import { getApiErrorDetail } from 'utils/api-error';

import ConfigurationLayout from 'components/configuration/ConfigurationLayout';
import ConfigurationHeader from 'components/configuration/ConfigurationHeader';
import ConfigurationButton from 'components/configuration/ConfigurationButton';
type ToolTab = 'tool' | 'mcp-server' | 'composio' | 'open-connectors' | 'blocks-tools';

const TOOL_TABS: ToolTab[] = ['tool', 'mcp-server', 'composio', 'open-connectors', 'blocks-tools'];

const getTabFromSearchParams = (searchParams: URLSearchParams): ToolTab => {
  const tab = searchParams.get('tab');
  return TOOL_TABS.includes(tab as ToolTab) ? (tab as ToolTab) : 'composio';
};

const PAGE_SIZE_OPTIONS = [10, 20, 50];

function isToolsPageSynced(
  data: IGetToolsResponse | undefined,
  page: number,
  pageSize: number,
): data is IGetToolsResponse {
  if (!data || data.page !== page) {
    return false;
  }

  return data.page_size == null || data.page_size === pageSize;
}

export function Tools() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const configurationSettings = useConfigStore((state) => state.configurationSettings);
  const projectKey = configurationSettings?.blocksKey || '';
  const isAdmin = projectKey === process.env.REACT_APP_PUBLIC_X_BLOCKS_KEY || true;

  const [activeTab, setActiveTab] = useState<ToolTab>(() => getTabFromSearchParams(searchParams));
  const [search, setSearch] = useState('');
  const [view, setView] = useState<ViewMode>('grid');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const trimmedSearch = search.trim().toLowerCase();
  const [blocksToolType, setBlocksToolType] = useState<'all' | ToolType>('all');
  const [blocksToolStatus, setBlocksToolStatus] = useState<'all' | ToolStatus>('all');
  const [toolToDelete, setToolToDelete] = useState<ITool | null>(null);
  const [isAddToolModalOpen, setIsAddToolModalOpen] = useState(false);
  const [isAddMcpServerModalOpen, setIsAddMcpServerModalOpen] = useState(false);
  const [isAddComposioModalOpen, setIsAddComposioModalOpen] = useState(false);
  const [isAddOpenConnectorModalOpen, setIsAddOpenConnectorModalOpen] = useState(false);
  const [isImportMcpModalOpen, setIsImportMcpModalOpen] = useState(false);
  const [selectedConnectorTool, setSelectedConnectorTool] = useState<ITool | null>(null);
  const [selectedOpenConnectorTool, setSelectedOpenConnectorTool] = useState<ITool | null>(null);

  useEffect(() => {
    setActiveTab(getTabFromSearchParams(searchParams));
  }, [searchParams]);

  const selectedToolType =
    activeTab === 'tool'
      ? 'api'
      : activeTab === 'mcp-server'
        ? 'mcp_server'
        : activeTab === 'composio'
          ? 'composio'
          : activeTab === 'open-connectors'
            ? 'open_connector'
            : null;

  const isBlocksToolsTab = activeTab === 'blocks-tools';

  const toolsQuery = useGetTools(
    {
      tool_type: selectedToolType,
      page,
      page_size: pageSize,
      project_key: projectKey,
    },
    { enabled: !isBlocksToolsTab },
  );

  const openToolsQuery = useGetOpenTools(
    {
      tool_type: blocksToolType === 'all' ? null : blocksToolType,
      tool_status: blocksToolStatus === 'all' ? null : blocksToolStatus,
      page,
      page_size: pageSize,
      project_key: projectKey,
    },
    { enabled: isBlocksToolsTab },
  );

  const allToolsQuery = useGetTools({
    page: 1,
    page_size: 50,
    project_key: projectKey,
  });

  const deleteToolMutation = useDeleteTool();
  const currentQuery = isBlocksToolsTab ? openToolsQuery : toolsQuery;
  const currentData = currentQuery.data;
  const isListLoading =
    currentQuery.isPending ||
    currentQuery.isFetching ||
    !isToolsPageSynced(currentData, page, pageSize);

  const displayData = useMemo(
    () => (isToolsPageSynced(currentData, page, pageSize) ? currentData : undefined),
    [currentData, page, pageSize],
  );

  const filteredDisplayData = useMemo(() => {
    if (!displayData) {
      return undefined;
    }

    if (!trimmedSearch) {
      return displayData;
    }

    const tools = displayData.tools.filter(
      (tool) =>
        tool.name.toLowerCase().includes(trimmedSearch) ||
        (tool.description || '').toLowerCase().includes(trimmedSearch),
    );

    return {
      ...displayData,
      tools,
      total_count: tools.length,
      total_pages: 1,
    };
  }, [displayData, trimmedSearch]);

  useEffect(() => {
    setPage(1);
  }, [trimmedSearch, activeTab]);

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPage(1);
  };

  const allTools = allToolsQuery.data?.tools || [];
  const userApiToolsCount = allTools.filter((tool) => tool.type === 'api' && !tool.is_open_tool).length;
  const userMcpServerCount = allTools.filter((tool) => tool.type === 'mcp_server' && !tool.is_open_tool).length;
  const userComposioToolsCount = allTools.filter((tool) => tool.type === 'composio' && !tool.is_open_tool).length;
  const userOpenConnectorToolsCount = allTools.filter(
    (tool) => tool.type === 'open_connector' && !tool.is_open_tool
  ).length;
  const blocksToolsCount = allTools.filter((tool) => tool.is_open_tool).length;

  const tabs = useMemo(
    () =>
      [
        {
          value: 'composio' as const,
          label: t('CONNECTOR') || 'Connector',
          count: userComposioToolsCount,
        },
        {
          value: 'mcp-server' as const,
          label: t('MCP', 'MCP'),
          count: userMcpServerCount,
        },
        {
          value: 'tool' as const,
          label: t('API') || 'API',
          count: userApiToolsCount,
        },
        {
          value: 'open-connectors' as const,
          label: t('OPEN_CONNECTORS') || 'Open Connectors',
          count: userOpenConnectorToolsCount,
        },
        {
          value: 'blocks-tools' as const,
          label: t('BLOCKS_TOOLS') || 'Blocks Tools',
          count: blocksToolsCount,
        },
      ] satisfies Array<{
        value: ToolTab;
        label: string;
        count: number;
      }>,
    [
      t,
      userApiToolsCount,
      userMcpServerCount,
      userComposioToolsCount,
      userOpenConnectorToolsCount,
      blocksToolsCount,
    ]
  );

  const showAddButton = activeTab !== 'blocks-tools' && (isAdmin);
  const showImportButton = activeTab === 'mcp-server' && showAddButton;
  const showLimitMessage =
    activeTab !== 'blocks-tools' &&
    !isAdmin &&
    ((activeTab === 'mcp-server' && userMcpServerCount >= 5) ||
      (activeTab === 'composio' && userComposioToolsCount >= 5) ||
      (activeTab === 'open-connectors' && userOpenConnectorToolsCount >= 5) ||
      (activeTab === 'tool' && userApiToolsCount >= 5));

  const handleTabChange = (value: string) => {
    const tab = value as ToolTab;
    setActiveTab(tab);
    setSearchParams({ tab });
  };

  const handleAddClick = () => {
    if (activeTab === 'mcp-server') {
      setIsAddMcpServerModalOpen(true);
      return;
    }

    if (activeTab === 'composio') {
      setIsAddComposioModalOpen(true);
      return;
    }

    if (activeTab === 'open-connectors') {
      setIsAddOpenConnectorModalOpen(true);
      return;
    }

    if (activeTab === 'tool') {
      setIsAddToolModalOpen(true);
    }
  };

  const handleOpenToolDetails = (tool: ITool) => {
    if (tool.type === 'composio') {
      setSelectedConnectorTool(tool);
      return;
    }

    if (tool.type === 'open_connector') {
      setSelectedOpenConnectorTool(tool);
      return;
    }

    navigate(`/tool-details/${encodeURIComponent(tool.id)}`, {
      state: { tool, fromTab: activeTab },
    });
  };

  const handleDeleteTool = async () => {
    if (!toolToDelete || !projectKey) return;

    try {
      const result = await deleteToolMutation.mutateAsync({
        toolId: toolToDelete.id,
        projectKey,
        isBlocksTool: false,
      });

      if (result.is_success === false) {
        throw new Error(result.message || result.detail || 'Failed to delete tool');
      }

      toast({
        variant: 'success',
        title: t('TOOL_DELETED') || 'Tool deleted',
        description:
          t('TOOL_REMOVED_SUCCESSFULLY', {
            name: toolToDelete.name,
            defaultValue: '{{name}} was removed successfully.',
          }),
      });

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['tools'] }),
        queryClient.invalidateQueries({ queryKey: ['tools', 'open-tools'] }),
      ]);
      setToolToDelete(null);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: t('DELETE_FAILED') || 'Delete failed',
        description:
          getApiErrorDetail(error) ?? (t('FAILED_TO_DELETE_TOOL') || 'Failed to delete tool.'),
      });
    }
  };

  return (
    <>
      <ConfigurationLayout>
        <div className="flex w-full min-w-0 flex-col gap-6 border-b border-grey-100 pb-6 dark:border-border">
          <div className="flex w-full min-w-0 flex-col gap-4 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <ConfigurationHeader
              title={t('TOOLS') || 'Tools'}
              subtitle={
                t('TOOLS_PAGE_SUBTITLE', {
                  defaultValue:
                    'Connect APIs, MCP servers, and connectors to extend what your agents can do.',
                }) ||
                'Connect APIs, MCP servers, and connectors to extend what your agents can do.'
              }
              icon={
                <ToolsIcon
                  width={20}
                  height={20}
                  strokeWidth={1.75}
                  className="text-grey-900 dark:text-foreground"
                />
              }
            />

            {showAddButton || showImportButton || showLimitMessage ? (
              <div className="flex w-full min-w-0 flex-wrap items-center justify-end gap-3 sm:w-auto sm:shrink-0">
                {!showAddButton && showLimitMessage ? (
                  <span className="min-w-0 max-w-md text-sm text-muted-foreground">
                    {t('5_OF_5_MY_TOOLS_CREATED') ||
                      '5 of 5 My Tools created. Please, delete one to add more'}
                  </span>
                ) : null}

                {showImportButton ? (
                  <ConfigurationButton
                    variant="outline"
                    icon={<Upload className="h-4 w-4" />}
                    onClick={() => setIsImportMcpModalOpen(true)}
                  >
                    {t('IMPORT_FROM_JSON', 'Import from JSON')}
                  </ConfigurationButton>
                ) : null}

                {showAddButton ? (
                  <ConfigurationButton
                    icon={<Plus className="h-4 w-4" />}
                    onClick={handleAddClick}
                  >
                    {t('ADD_INTEGRATION') || 'Add Integration'}
                  </ConfigurationButton>
                ) : null}
              </div>
            ) : null}
          </div>

          <div className="flex h-12 w-full min-w-0 items-center gap-3 rounded-lg border border-grey-100 bg-white p-3 sm:max-w-[460px] dark:border-border dark:bg-card">
            <div className="flex h-6 w-6 shrink-0 items-center justify-center p-[3px]">
              <Search width={15} height={15} strokeWidth={1.75} className="text-grey-400" />
            </div>
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('SEARCH_TOOLS') || 'Search tools...'}
              className="h-full min-h-0 flex-1 border-0 bg-transparent p-0 text-sm font-normal leading-[1.2] shadow-none placeholder:text-grey-400 focus-visible:ring-0 dark:bg-transparent"
            />
          </div>
        </div>

        <div className="flex min-w-0 flex-col gap-10 pb-8">
          <div className="flex w-full min-w-0 flex-col gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4">
            <div className="inline-flex max-w-full shrink-0 flex-nowrap items-center gap-2 overflow-x-auto">
              {tabs.map((tab) => {
                const isActive = activeTab === tab.value;

                return (
                  <button
                    key={tab.value}
                    type="button"
                    onClick={() => handleTabChange(tab.value)}
                    className={cn(
                      'inline-flex h-10 shrink-0 items-center rounded-lg border px-3 text-sm font-normal leading-[1.2] transition-colors',
                      isActive
                        ? 'border-grey-100 bg-grey-50 text-grey-900 dark:border-true-white/40 dark:bg-true-white/15 dark:text-true-white'
                        : 'border-transparent text-grey-500 hover:bg-grey-50 hover:text-grey-900 dark:text-true-white/75 dark:hover:bg-true-white/10 dark:hover:text-true-white'
                    )}
                  >
                    {tab.label}
                    <span
                      className={cn(
                        'ml-1.5 text-xs',
                        isActive
                          ? 'text-grey-500 dark:text-true-white/80'
                          : 'text-grey-500 dark:text-true-white/55'
                      )}
                    >
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>

            <div
              className={cn(
                'flex w-full min-w-0 gap-3 lg:w-auto lg:shrink-0',
                activeTab === 'blocks-tools'
                  ? 'flex-col md:flex-row md:items-center md:justify-between lg:flex-row lg:flex-nowrap lg:items-center lg:justify-end'
                  : 'flex-col sm:flex-row sm:items-center sm:justify-end lg:flex-row lg:justify-end'
              )}
            >
              {activeTab === 'blocks-tools' ? (
                <div className="grid w-full min-w-0 grid-cols-1 gap-3 sm:grid-cols-2 md:flex md:w-auto md:flex-row lg:shrink-0">
                  <Select
                    value={blocksToolType}
                    onValueChange={(value) => {
                      setBlocksToolType(value as 'all' | ToolType);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-9 w-full min-w-0 md:w-[170px]">
                      <SelectValue placeholder={t('TOOL_TYPE') || 'Tool type'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('ALL_TYPES') || 'All types'}</SelectItem>
                      <SelectItem value="api">{t('API') || 'API'}</SelectItem>
                      <SelectItem value="mcp_server">{t('MCP_SERVER', 'MCP Server')}</SelectItem>
                      <SelectItem value="composio">{t('CONNECTOR') || 'Connector'}</SelectItem>
                      <SelectItem value="open_connector">{t('OPEN_CONNECTORS') || 'Open Connectors'}</SelectItem>
                      <SelectItem value="webhook">{t('WEBHOOK') || 'Webhook'}</SelectItem>
                      <SelectItem value="graphql">{t('GRAPHQL') || 'GraphQL'}</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={blocksToolStatus}
                    onValueChange={(value) => {
                      setBlocksToolStatus(value as 'all' | ToolStatus);
                      setPage(1);
                    }}
                  >
                    <SelectTrigger className="h-9 w-full min-w-0 md:w-[170px]">
                      <SelectValue placeholder={t('STATUS') || 'Status'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('ALL_STATUS') || 'All status'}</SelectItem>
                      <SelectItem value="active">{t('ACTIVE') || 'Active'}</SelectItem>
                      <SelectItem value="inactive">{t('INACTIVE') || 'Inactive'}</SelectItem>
                      <SelectItem value="deprecated">{t('DEPRECATED') || 'Deprecated'}</SelectItem>
                      <SelectItem value="maintenance">{t('MAINTENANCE') || 'Maintenance'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ) : null}

              {/* <div className="flex h-9 shrink-0 items-center self-end rounded-md border border-input bg-background p-0.5 shadow-sm sm:self-auto">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setView('list')}
                  className={cn('h-8 w-8', view === 'list' ? 'bg-muted shadow-sm' : '')}
                  aria-label={t('LIST_VIEW') || 'List view'}
                >
                  <List className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setView('grid')}
                  className={cn('h-8 w-8', view === 'grid' ? 'bg-muted shadow-sm' : '')}
                  aria-label={t('GRID_VIEW') || 'Grid view'}
                >
                  <LayoutGrid className="h-4 w-4" />
                </Button>
              </div> */}
            </div>
          </div>

          {activeTab === 'blocks-tools' ? (
            <BlocksToolsList
              data={filteredDisplayData}
              isLoading={isListLoading}
              isAdmin={isAdmin}
              view={view}
              projectKey={projectKey}
              onToolSelect={handleOpenToolDetails}
              onDeleteTool={setToolToDelete}
              isDeletingToolId={deleteToolMutation.isPending ? toolToDelete?.id : null}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
            />
          ) : (
            <ToolsList
              data={filteredDisplayData}
              isLoading={isListLoading}
              view={view}
              activeTab={activeTab}
              onAddClick={handleAddClick}
              onImportClick={showImportButton ? () => setIsImportMcpModalOpen(true) : undefined}
              onToolSelect={handleOpenToolDetails}
              onDeleteTool={setToolToDelete}
              isDeletingToolId={deleteToolMutation.isPending ? toolToDelete?.id : null}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              projectKey={projectKey}
              isAdmin={isAdmin}
            />
          )}
        </div>
      </ConfigurationLayout>

      <AlertDialog open={!!toolToDelete} onOpenChange={(open) => !open && setToolToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('DELETE_TOOL') || 'Delete tool?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {toolToDelete
                ? t('REMOVE_TOOL_FROM_TENANT', {
                  name: toolToDelete.name,
                  defaultValue: 'This will permanently remove "{{name}}" from this tenant.',
                })
                : t('THIS_ACTION_CANNOT_BE_UNDONE') || 'This action cannot be undone.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteToolMutation.isPending}>
              {t('CANCEL') || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteTool} disabled={deleteToolMutation.isPending}>
              {deleteToolMutation.isPending ? t('DELETING') || 'Deleting...' : t('DELETE') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <EditToolModal mode="create" open={isAddToolModalOpen} onOpenChange={setIsAddToolModalOpen} />
      <AddMcpServerModal
        open={isAddMcpServerModalOpen}
        onOpenChange={setIsAddMcpServerModalOpen}
        projectKey={projectKey}
      />
      <ImportMcpServersModal
        open={isImportMcpModalOpen}
        onOpenChange={setIsImportMcpModalOpen}
        projectKey={projectKey}
      />
      <EditComposioToolModal
        mode="create"
        open={isAddComposioModalOpen}
        onOpenChange={setIsAddComposioModalOpen}
      />
      <EditOpenConnectorToolModal
        mode="create"
        open={isAddOpenConnectorModalOpen}
        onOpenChange={setIsAddOpenConnectorModalOpen}
      />
      <OpenConnectorDetailsModal
        tool={selectedOpenConnectorTool}
        projectKey={projectKey}
        onClose={() => setSelectedOpenConnectorTool(null)}
      />
      <ConnectorDetailsModal
        tool={selectedConnectorTool}
        projectKey={projectKey}
        onClose={() => setSelectedConnectorTool(null)}
      />
    </>
  );
}
